import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { GitProvider } from "@/utils/gitProviderUtils";
import {
  getUnexpiredToken,
  useIsGitlabConnected,
  useIsGithubConnected,
  useIsBitbucketConnected,
  useActiveProject,
} from "@/utils/utils";
import { axiosPost, axiosGet } from "@/utils/axiosWrapper";
import { ChevronDownIcon, CircleAlert, Loader2 } from "lucide-react";
import React, { useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import TempoModal from "./Core/TempoModal";
import { useUser, useSession } from "@clerk/clerk-react";
import { GitHubLogoIcon } from "@radix-ui/react-icons";
import { useDispatch } from "react-redux";
import { onProjectsSnapshot } from "@/store/supabaseSlice";
import { useGitProvider } from "@/hooks/useGithubPermissions";

// Org interface (copied from GitSettings.tsx)
export interface Org {
  login: string;
  id: string;
  avatar_url: string | null;
  groupNamespaceId?: string | null;
}

interface CreateRepositoryModalProps {
  isOpen: boolean;
  onClose: () => void;
  initialSelectedOrg: Org | null;
  onRepoCreated: () => void;
  canvasId: string;
  originatingProvider?: GitProvider;
}

interface OrgWithProvider {
  org: Org;
  provider: GitProvider;
  id: string;
}

const CreateRepositoryModal: React.FC<CreateRepositoryModalProps> = ({
  isOpen,
  onClose,
  initialSelectedOrg,
  onRepoCreated,
  canvasId,
  originatingProvider,
}) => {
  const { user } = useUser();
  const { session } = useSession();
  const dispatch = useDispatch();

  const project: any = useActiveProject();
  const isGitlabConnected = useIsGitlabConnected();
  const isGithubConnected = useIsGithubConnected();
  const isBitbucketConnected = useIsBitbucketConnected();

  const {
    githubPermissionState,
    checkGithubPermissions,
    startPolling,
    resetPermissionState,
    isPolling,
    isPermissionGranted,
  } = useGitProvider(originatingProvider || GitProvider.GITHUB);

  const [userGithubOrgsList, setUserGithubOrgsList] = useState<Org[]>([]);
  const [userGitlabOrgsList, setUserGitlabOrgsList] = useState<Org[]>([]);
  const [userBitbucketOrgsList, setUserBitbucketOrgsList] = useState<Org[]>([]);

  const [selectedOrgDetails, setSelectedOrgDetails] =
    useState<OrgWithProvider | null>(null);
  const [repoName, setRepoName] = useState("");
  const [repoIsPublic, setRepoIsPublic] = useState(true);

  const [isCreatingRepo, setIsCreatingRepo] = useState(false);

  useEffect(() => {
    const fetchUserOrgs = async () => {
      if (!session || !user) return;
      try {
        const supabaseJWT = await getUnexpiredToken(session);
        if (originatingProvider === GitProvider.GITLAB && isGitlabConnected) {
          const url = `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/gitProvider/user/orgs?gitProvider=gitlab`;
          const externalGitlabAccount = user.externalAccounts?.find(
            (account: any) =>
              account.provider === "gitlab" ||
              account.provider === "oauth_gitlab"
          );
          if (externalGitlabAccount?.username) {
            const gitlabUserOrg = {
              login: externalGitlabAccount.username,
              id: externalGitlabAccount.id as string,
              avatar_url: externalGitlabAccount.imageUrl,
              groupNamespaceId: null,
            };
            const res = await axiosGet(url, {
              headers: { Authorization: `Bearer ${supabaseJWT}` },
              errorMessage: "Failed to fetch GitLab organizations",
              showToast: false,
            });
            const gitlabOrgs = res?.map((org: any) => ({
              login: org.name,
              id: org.id,
              avatar_url: org.avatar_url,
              groupNamespaceId: org.id,
            }));
            setUserGitlabOrgsList([gitlabUserOrg, ...gitlabOrgs]);
          } else {
            setUserGitlabOrgsList([]);
          }
        }
        if (originatingProvider === GitProvider.GITHUB && isGithubConnected) {
          const externalGithubAccount = user.externalAccounts?.find(
            (account: any) => account.provider === "github"
          );
          if (externalGithubAccount?.username) {
            const githubUserOrg = {
              login: externalGithubAccount.username,
              id: externalGithubAccount.id as string,
              avatar_url: externalGithubAccount.imageUrl,
            };
            const url = `${
              import.meta.env.VITE_BACKEND_BASE_URL
            }/gitProvider/user/orgs?gitProvider=github`;
            const res = await axiosGet(url, {
              headers: { Authorization: `Bearer ${supabaseJWT}` },
              errorMessage: "Failed to fetch GitHub organizations",
              showToast: false,
            });
            setUserGithubOrgsList([githubUserOrg, ...(res || [])]);
          } else {
            setUserGithubOrgsList([]);
          }
        }

        if (
          originatingProvider === GitProvider.BITBUCKET &&
          isBitbucketConnected
        ) {
          const externalBitbucketAccount = user?.externalAccounts?.find(
            (account: any) => account.provider === "bitbucket"
          );

          if (!externalBitbucketAccount || !externalBitbucketAccount.username) {
            console.error("No Bitbucket account found");
            return;
          }

          const url = `${
            import.meta.env.VITE_BACKEND_BASE_URL
          }/gitProvider/user/orgs?gitProvider=bitbucket`;
          const res = await axiosGet(url, {
            headers: { Authorization: `Bearer ${supabaseJWT}` },
            errorMessage: "Failed to fetch Bitbucket organizations",
            showToast: false,
          });

          const bitbucketOrgs = res?.values
            ? res.values.map((org: any) => ({
                login: org?.name,
                id: org?.uuid,
                avatar_url: org?.links?.avatar?.href,
              }))
            : [];

          setUserBitbucketOrgsList(bitbucketOrgs);
        }
      } catch (err) {
        console.error("Failed to fetch user orgs in modal", err);
        setUserGithubOrgsList([]);
        setUserGitlabOrgsList([]);
        setUserBitbucketOrgsList([]);
      }
    };
    if (isOpen) fetchUserOrgs();
  }, [
    isOpen,
    Boolean(session),
    user,
    isGitlabConnected,
    isGithubConnected,
    isBitbucketConnected,
  ]);

  const allOrgsForDropdown = useMemo(() => {
    const githubOrgsWithProvider: OrgWithProvider[] = userGithubOrgsList.map(
      (org) => ({ org, provider: GitProvider.GITHUB, id: `gh-${org.id}` })
    );
    const gitlabOrgsWithProvider: OrgWithProvider[] = userGitlabOrgsList.map(
      (org) => ({ org, provider: GitProvider.GITLAB, id: `gl-${org.id}` })
    );
    const bitbucketOrgsWithProvider: OrgWithProvider[] =
      userBitbucketOrgsList.map((org) => ({
        org,
        provider: GitProvider.BITBUCKET,
        id: `bb-${org.id}`,
      }));
    return [
      ...githubOrgsWithProvider,
      ...gitlabOrgsWithProvider,
      ...bitbucketOrgsWithProvider,
    ];
  }, [userGithubOrgsList, userGitlabOrgsList, userBitbucketOrgsList]);

  const displayedOrgsForDropdown = useMemo(() => {
    if (originatingProvider) {
      return allOrgsForDropdown.filter(
        (orgDetails) => orgDetails.provider === originatingProvider
      );
    }
    return allOrgsForDropdown;
  }, [allOrgsForDropdown, originatingProvider]);

  useEffect(() => {
    if (isOpen) {
      let determinedDefault: OrgWithProvider | null = null;
      if (initialSelectedOrg && displayedOrgsForDropdown.length > 0) {
        determinedDefault =
          displayedOrgsForDropdown.find(
            (o) =>
              o.org.id === initialSelectedOrg.id &&
              (initialSelectedOrg.groupNamespaceId !== undefined
                ? o.provider === GitProvider.GITLAB
                : o.provider === GitProvider.GITHUB) &&
              o.org.login === initialSelectedOrg.login
          ) || null;
      }
      if (determinedDefault === null && displayedOrgsForDropdown.length > 0) {
        determinedDefault = displayedOrgsForDropdown[0];
      }
      setSelectedOrgDetails(determinedDefault);
      setRepoName("");
      setRepoIsPublic(true);
      resetPermissionState();
    }
  }, [
    isOpen,
    initialSelectedOrg,
    displayedOrgsForDropdown,
    originatingProvider,
    resetPermissionState,
  ]);

  useEffect(() => {
    if (isOpen && isGithubConnected) {
      checkGithubPermissions(true);
    }
  }, [isOpen, isGithubConnected, checkGithubPermissions]);

  useEffect(() => {
    if (!isOpen) {
      resetPermissionState();
    }
  }, [isOpen, resetPermissionState]);

  const handlePushToNewRepo = async () => {
    setIsCreatingRepo(true);

    if (!session || !selectedOrgDetails || !canvasId || !user) return;
    const supabaseJWT = await getUnexpiredToken(session);
    const { org: selectedOrgObject, provider: selectedProvider } =
      selectedOrgDetails;
    let actualOrgName: string | null = null;
    if (selectedProvider === GitProvider.GITHUB) {
      if (
        userGithubOrgsList.length > 0 &&
        selectedOrgObject.id !== userGithubOrgsList[0].id
      ) {
        actualOrgName = selectedOrgObject.login;
      }
    } else if (selectedProvider === GitProvider.GITLAB) {
      if (
        userGitlabOrgsList.length > 0 &&
        selectedOrgObject.id !== userGitlabOrgsList[0].id
      ) {
        actualOrgName = selectedOrgObject.login;
      }
    } else if (selectedProvider === GitProvider.BITBUCKET) {
      actualOrgName = selectedOrgObject.login;
    }
    const repoDetails = {
      repoName,
      repoPrivate: !repoIsPublic,
      authorEmail: user.primaryEmailAddress?.emailAddress,
      authorName: user.username,
      orgName: actualOrgName,
      gitProvider: selectedProvider,
      groupNamespaceId:
        selectedProvider === GitProvider.GITLAB
          ? selectedOrgObject.groupNamespaceId
          : undefined,
    };

    try {
      const result = await axiosPost(
        `${
          import.meta.env.VITE_BACKEND_BASE_URL
        }/canvases/${canvasId}/connectRepo`,
        repoDetails,
        {
          headers: { Authorization: `Bearer ${supabaseJWT}` },
          errorMessage: "Failed to connect to new repository",
          showToast: true,
          throwOnError: false,
        }
      );

      if (result) {
        toast.success("Connected to new repository!");

        const ownerSegment = actualOrgName || selectedOrgDetails.org.login;
        const newRepoPath = `${ownerSegment}/${repoName}`;
        dispatch(
          onProjectsSnapshot({
            newItems: [
              {
                ...project,
                repoPath: newRepoPath,
              },
            ],
          })
        );

        onRepoCreated();
        onClose();
      }
    } finally {
      setIsCreatingRepo(false);
    }
  };

  if (!isOpen) return null;

  const permittedToCreateRepo = isPermissionGranted;
  return (
    <TempoModal
      key="create-repo-modal-unified"
      className="git-discard-modal min-w-[400px]"
      header="Create Repository"
      onClose={onClose}
    >
      <div className="w-full flex flex-col gap-y-3">
        {selectedOrgDetails?.provider === GitProvider.GITHUB &&
          !permittedToCreateRepo &&
          githubPermissionState.link && (
            <div className="w-full flex flex-col p-2 border-[1px] border-[#2F3035] rounded-sm gap-1 items-start bg-[#2F3035]/20">
              <>
                <div className="flex flex-row gap-x-2 items-start w-full">
                  <CircleAlert className="max-w-4 max-h-4 min-w-4 min-h-4 text-icons-alert" />
                  <div>
                    <div className="text-xs text-text-primary font-medium">
                      Github needs permission
                    </div>
                    <div className="text-xs text-text-secondary">
                      You haven't given GitHub permission to create a
                      repository. Install Tempo Github app with the correct
                      permissions, then refresh this modal.
                    </div>
                  </div>
                </div>
                <div className="text-xs text-primary-200 pl-6 mt-1 flex items-center gap-x-2">
                  <span
                    className={cn(
                      "text-primary-200 cursor-pointer hover:underline",
                      isPolling &&
                        "opacity-50 cursor-not-allowed pointer-events-none"
                    )}
                    onClick={() => {
                      if (githubPermissionState.link) {
                        window.open(githubPermissionState.link, "_blank");
                      }
                    }}
                  >
                    Install
                  </span>{" "}
                  {isPolling ? (
                    <span className="inline-flex items-center gap-x-1">
                      <Loader2 className="w-3 h-3 text-primary-0 animate-spin" />
                      Checking...
                    </span>
                  ) : (
                    <span
                      className="text-primary-200 cursor-pointer hover:underline"
                      onClick={startPolling}
                    >
                      Refresh
                    </span>
                  )}
                </div>
              </>
            </div>
          )}
        <div className="w-full text-sm flex flex-col gap-y-3">
          <div className="flex flex-col flex-grow gap-y-1">
            <h3 className="text-sm font-medium mb-1">
              Push changes from this template to a new repository
            </h3>
            <div className="flex flex-row gap-x-2 items-center">
              <div className="flex-1">
                <DropdownMenu>
                  <DropdownMenuTrigger
                    asChild
                    className="w-full"
                    disabled={!isGithubConnected && !isGitlabConnected}
                  >
                    <div
                      className={cn(
                        "bg-background-secondary w-full text-left flex justify-start min-h-[28px] px-2 py-1.5 border border-secondary-n-800 rounded-sm items-start cursor-pointer",
                        !isGithubConnected &&
                          !isGitlabConnected &&
                          "opacity-50 cursor-not-allowed"
                      )}
                    >
                      <span className="text-xs font-medium flex gap-2 w-full items-center justify-start">
                        {selectedOrgDetails?.provider === GitProvider.GITHUB ? (
                          <GitHubLogoIcon className="w-4 h-4" />
                        ) : selectedOrgDetails?.provider ===
                          GitProvider.GITLAB ? (
                          <img
                            src="/icons/gitlab-logo.svg"
                            className="w-4 h-4 rounded-full"
                            alt={`Gitlab logo`}
                          />
                        ) : selectedOrgDetails?.provider ===
                          GitProvider.BITBUCKET ? (
                          <img
                            src="/icons/bitbucket-logo.svg"
                            className="w-3 h-3"
                            alt={`Bitbucket logo`}
                          />
                        ) : selectedOrgDetails ? (
                          <div className="w-4 h-4 rounded-full bg-gray-500 flex items-center justify-center text-white text-2xs">
                            {selectedOrgDetails.org.login
                              .substring(0, 1)
                              .toUpperCase()}
                          </div>
                        ) : null}
                        <p className="flex-1 whitespace-normal leading-normal">
                          {selectedOrgDetails?.org.login || "Select Owner"}
                        </p>
                        <ChevronDownIcon className="w-3.5 h-3.5 ml-auto text-secondary-n-800" />
                      </span>
                    </div>
                  </DropdownMenuTrigger>
                  <DropdownMenuContent
                    className="z-[9999] dropdown-content-width-full"
                    align="start"
                    sideOffset={5}
                    alignOffset={0}
                  >
                    {displayedOrgsForDropdown.map((item) => (
                      <DropdownMenuItem
                        key={item.id}
                        className="flex flex-row gap-x-2 items-center justify-start text-xs"
                        onSelect={() => {
                          if (
                            (item.provider === GitProvider.GITHUB &&
                              isGithubConnected) ||
                            (item.provider === GitProvider.GITLAB &&
                              isGitlabConnected) ||
                            (item.provider === GitProvider.BITBUCKET &&
                              isBitbucketConnected)
                          ) {
                            setSelectedOrgDetails(item);
                          } else {
                            const providerName =
                              item.provider.charAt(0).toUpperCase() +
                              item.provider.slice(1);
                            toast.error(
                              `Please connect your ${providerName} account first.`
                            );
                          }
                        }}
                        disabled={
                          (item.provider === GitProvider.GITHUB &&
                            !isGithubConnected) ||
                          (item.provider === GitProvider.GITLAB &&
                            !isGitlabConnected) ||
                          (item.provider === GitProvider.BITBUCKET &&
                            !isBitbucketConnected)
                        }
                      >
                        <div className="flex items-center gap-2 flex-1">
                          {item.provider === GitProvider.GITHUB ? (
                            <GitHubLogoIcon className="w-4 h-4" />
                          ) : item.provider === GitProvider.GITLAB ? (
                            <img
                              src="/icons/gitlab-logo.svg"
                              className="w-4 h-4"
                              alt="GitLab"
                            />
                          ) : item.provider === GitProvider.BITBUCKET ? (
                            <img
                              src="/icons/bitbucket-logo.svg"
                              className="w-4 h-4"
                              alt="Bitbucket"
                            />
                          ) : null}
                          <span>{item.org.login}</span>
                        </div>
                      </DropdownMenuItem>
                    ))}
                  </DropdownMenuContent>
                </DropdownMenu>
              </div>
              <span className="text-xl text-center flex-shrink-0">/</span>
              <div className="flex-1">
                <input
                  className={cn(
                    "text-xs bg-background-secondary rounded-sm px-2 py-1 w-full h-7 min-h-0 leading-none placeholder-text-muted border-[1px] border-secondary-n-800",
                    ((selectedOrgDetails?.provider === GitProvider.GITHUB &&
                      !permittedToCreateRepo) ||
                      (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                        !isGitlabConnected) ||
                      (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                        !isBitbucketConnected)) &&
                      "opacity-50 cursor-not-allowed"
                  )}
                  placeholder="repository-name"
                  value={repoName}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => {
                    let newValue = e.target.value;
                    if (
                      selectedOrgDetails?.provider === GitProvider.BITBUCKET
                    ) {
                      newValue = newValue.toLowerCase();
                    }

                    setRepoName(newValue);
                  }}
                  onKeyDown={(e: React.KeyboardEvent<HTMLInputElement>) => {
                    if (e.key === "Enter") {
                      e.preventDefault();
                      if (
                        !isCreatingRepo &&
                        (permittedToCreateRepo ||
                          selectedOrgDetails?.provider ===
                            GitProvider.GITLAB) &&
                        repoName
                      ) {
                        handlePushToNewRepo();
                      }
                    }
                  }}
                  disabled={
                    (selectedOrgDetails?.provider === GitProvider.GITHUB &&
                      !permittedToCreateRepo) ||
                    (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                      !isGitlabConnected) ||
                    (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                      !isBitbucketConnected)
                  }
                />
              </div>
            </div>
          </div>

          <div className="w-full">
            <div className="flex flex-row bg-background-secondary rounded-md w-fit p-0.5">
              <button
                className={cn(
                  `px-2 py-1 text-xs font-medium text-text-primary ${
                    repoIsPublic
                      ? "bg-background-tertiary "
                      : "bg-transparent text-text-muted"
                  } rounded-md transition-colors`,
                  ((selectedOrgDetails?.provider === GitProvider.GITHUB &&
                    !permittedToCreateRepo) ||
                    (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                      !isGitlabConnected) ||
                    (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                      !isBitbucketConnected)) &&
                    "opacity-50 cursor-not-allowed"
                )}
                onClick={() => setRepoIsPublic(true)}
                disabled={
                  (selectedOrgDetails?.provider === GitProvider.GITHUB &&
                    !permittedToCreateRepo) ||
                  (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                    !isGitlabConnected) ||
                  (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                    !isBitbucketConnected)
                }
              >
                Public
              </button>
              <button
                className={cn(
                  `px-2 py-1 text-xs font-medium text-text-primary ${
                    !repoIsPublic
                      ? "bg-background-tertiary "
                      : "bg-transparent text-text-muted"
                  } rounded-md transition-colors`,
                  ((selectedOrgDetails?.provider === GitProvider.GITHUB &&
                    !permittedToCreateRepo) ||
                    (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                      !isGitlabConnected) ||
                    (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                      !isBitbucketConnected)) &&
                    "opacity-50 cursor-not-allowed"
                )}
                onClick={() => setRepoIsPublic(false)}
                disabled={
                  (selectedOrgDetails?.provider === GitProvider.GITHUB &&
                    !permittedToCreateRepo) ||
                  (selectedOrgDetails?.provider === GitProvider.GITLAB &&
                    !isGitlabConnected) ||
                  (selectedOrgDetails?.provider === GitProvider.BITBUCKET &&
                    !isBitbucketConnected)
                }
              >
                Private
              </button>
            </div>
            <p className="text-2xs text-text-secondary">
              {repoIsPublic
                ? "Anyone can see this repo, you choose who can commit"
                : "Only you can commit to this repo"}
            </p>
          </div>
        </div>
        <div className="flex justify-end w-full gap-2">
          <Button
            variant="outline"
            className="text-xs px-3 py-0 h-7 min-h-0 bg-background-secondary"
            onClick={onClose}
          >
            Cancel
          </Button>
          <Button
            variant="secondary"
            className="text-xs px-3 py-0 h-7 min-h-0 leading-none bg-primary-0 hover:bg-primary-100 rounded-md"
            disabled={
              isCreatingRepo ||
              ((!permittedToCreateRepo || !repoName) &&
                selectedOrgDetails?.provider !== GitProvider.GITLAB &&
                selectedOrgDetails?.provider !== GitProvider.BITBUCKET)
            }
            onClick={() => {
              handlePushToNewRepo();
            }}
          >
            {isCreatingRepo ? (
              <>
                <Loader2 className="mr-2 h-3 w-3 animate-spin" />
                Creating...
              </>
            ) : (
              "Create"
            )}
          </Button>
        </div>
      </div>
    </TempoModal>
  );
};

export default CreateRepositoryModal;
